import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Clock, CheckCircle, XCircle, DollarSign, Send,
  User, MapPin, Calendar, FileText, Eye, MessageSquare, Trash2,
  Edit, Archive, RefreshCw, X, Plus, Calculator, Car, Package, Clock3,
  Camera, Image, Upload, Briefcase
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

// Quote Calculator Constants - 5 Core Services (Rides PRIMARY)
const PRICING_CONFIG = {
  base_rate: 35,  // Base hourly rate
  mileage_rate: 0.65,  // Per mile
  urgent_multiplier: 1.5,
  service_types: {
    rides: { label: 'Rides', base_rate: 25 },
    laundry: { label: 'Laundry Services', base_rate: 25 },
    courier: { label: 'Courier / Delivery', base_rate: 25 },
    errands: { label: 'Errands & Tasks', base_rate: 22 }
  },
  add_ons: {
    pet_care: { label: 'Pet Care', price: 20, category: 'errands' },
    grocery_shopping: { label: 'Grocery Shopping', price: 15, category: 'errands' },
    prescription_pickup: { label: 'Prescription Pickup', price: 10, category: 'errands' },
    heavy_lifting: { label: 'Heavy Lifting', price: 25, category: 'errands' },
    same_day: { label: 'Same Day Service', price: 30, category: 'all' },
    furniture_assembly: { label: 'Furniture Assembly', price: 35, category: 'errands' },
    picture_hanging: { label: 'Picture Hanging', price: 20, category: 'errands' },
    moving_help: { label: 'Moving Help', price: 45, category: 'errands' },
    yard_work: { label: 'Light Yard Work', price: 30, category: 'errands' },
    express_delivery: { label: 'Express Delivery', price: 15, category: 'courier' },
    multiple_stops: { label: 'Multiple Stops', price: 20, category: 'courier' },
    express_laundry: { label: 'Express Laundry', price: 15, category: 'laundry' }
  }
};

const CustomRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quotePrice, setQuotePrice] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteDuration, setQuoteDuration] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ preferred_date: '', preferred_time: '' });
  
  // Manual Request Form
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualRequest, setManualRequest] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    service_type: 'errands',
    service_description: '',
    preferred_date: '',
    preferred_time: '',
    address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    budget_range: '',
    special_instructions: '',
    is_urgent: false,
    photos: []
  });
  
  // Photo Management
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoType, setPhotoType] = useState('request'); // request, before, after
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoDescription, setNewPhotoDescription] = useState('');
  
  // Quote Calculator
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcData, setCalcData] = useState({
    service_type: 'errands',
    hours: 2,
    miles: 0,
    is_urgent: false,
    selected_addons: []
  });

  // Get base rate based on selected service
  const getBaseRate = () => {
    return PRICING_CONFIG.service_types[calcData.service_type]?.base_rate || PRICING_CONFIG.base_rate;
  };

  // Get relevant add-ons for selected service
  const getRelevantAddons = () => {
    return Object.entries(PRICING_CONFIG.add_ons).filter(([key, addon]) => 
      addon.category === 'all' || addon.category === calcData.service_type
    );
  };

  const statuses = [
    { value: 'pending', label: 'Pending', color: 'bg-[var(--warning)]/15 text-yellow-700', icon: Clock },
    { value: 'reviewing', label: 'Reviewing', color: 'bg-[#D4AF37]/10 text-[#D4AF37]', icon: Eye },
    { value: 'quoted', label: 'Quoted', color: 'bg-[#F4C2C2]/15 text-purple-700', icon: DollarSign },
    { value: 'accepted', label: 'Accepted', color: 'bg-[var(--success)]/15 text-green-700', icon: CheckCircle },
    { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    { value: 'declined', label: 'Declined', color: 'bg-[var(--error)]/15 text-red-700', icon: XCircle },
    { value: 'converted', label: 'Converted', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]', icon: XCircle },
    { value: 'archived', label: 'Archived', color: 'bg-slate-100 text-slate-700', icon: Archive }
  ];

  useEffect(() => {
    fetchData();
     
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const [requestsRes, statsRes] = await Promise.all([
        api.get(`/custom-requests${params}`),
        api.get('/custom-requests/stats')
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch custom requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuote = async () => {
    if (!quotePrice || parseFloat(quotePrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/custom-requests/${selectedRequest.id}/quote`, {
        quoted_price: parseFloat(quotePrice),
        quote_notes: quoteNotes || null,
        estimated_duration_hours: quoteDuration ? parseFloat(quoteDuration) : null
      });
      toast.success('Quote sent to customer!');
      setSelectedRequest(null);
      setQuotePrice('');
      setQuoteNotes('');
      setQuoteDuration('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/custom-requests/${requestId}/status?status=${newStatus}`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Delete this custom request? This action cannot be undone.')) return;
    try {
      await api.delete(`/custom-requests/${requestId}`);
      toast.success('Custom request deleted');
      fetchData();
      if (selectedRequest?.id === requestId) setSelectedRequest(null);
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleArchiveRequest = async (requestId) => {
    try {
      await api.put(`/custom-requests/${requestId}/status?status=archived`);
      toast.success('Request archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await api.put(`/custom-requests/${requestId}/status?status=approved`);
      toast.success('Request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleEditRequest = async () => {
    try {
      await api.put(`/custom-requests/${selectedRequest.id}`, editData);
      toast.success('Request updated');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleReschedule = async () => {
    try {
      await api.put(`/custom-requests/${selectedRequest.id}`, {
        preferred_date: rescheduleData.preferred_date,
        preferred_time: rescheduleData.preferred_time
      });
      toast.success('Request rescheduled');
      setShowRescheduleModal(false);
      setRescheduleData({ preferred_date: '', preferred_time: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule');
    }
  };

  // Photo management functions
  const handleAddPhoto = async () => {
    if (!newPhotoUrl || !selectedRequest) {
      toast.error('Please enter a photo URL');
      return;
    }
    try {
      await api.post(`/custom-requests/${selectedRequest.id}/photos`, {
        url: newPhotoUrl,
        description: newPhotoDescription,
        photo_type: photoType
      });
      toast.success('Photo added');
      setNewPhotoUrl('');
      setNewPhotoDescription('');
      setShowPhotoModal(false);
      fetchData();
      // Refresh selected request
      const res = await api.get(`/custom-requests`);
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (error) {
      toast.error('Failed to add photo');
    }
  };

  const handleRemovePhoto = async (photoId, type) => {
    if (!selectedRequest) return;
    try {
      await api.delete(`/custom-requests/${selectedRequest.id}/photos/${photoId}?photo_type=${type}`);
      toast.success('Photo removed');
      fetchData();
      // Refresh selected request
      const res = await api.get(`/custom-requests`);
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Calculate quote from calculator
  const calculateQuote = () => {
    const baseRate = getBaseRate();
    const baseAmount = baseRate * calcData.hours;
    const mileageAmount = PRICING_CONFIG.mileage_rate * calcData.miles;
    const addonsAmount = calcData.selected_addons.reduce((sum, addon) => {
      return sum + (PRICING_CONFIG.add_ons[addon]?.price || 0);
    }, 0);
    
    let subtotal = baseAmount + mileageAmount + addonsAmount;
    let urgentFee = 0;
    if (calcData.is_urgent) {
      urgentFee = subtotal * 0.5;
      subtotal += urgentFee;
    }
    
    return {
      baseRate,
      baseAmount,
      mileageAmount,
      addonsAmount,
      urgentFee,
      total: Math.round(subtotal * 100) / 100
    };
  };

  const applyCalculatedQuote = () => {
    const quote = calculateQuote();
    setQuotePrice(quote.total.toString());
    setQuoteDuration(calcData.hours.toString());
    
    // Build detailed notes from calculation
    const notes = [];
    const serviceLabel = PRICING_CONFIG.service_types[calcData.service_type]?.label || 'Service';
    notes.push(`Service: ${serviceLabel}`);
    notes.push(`Base: ${calcData.hours}hrs × $${quote.baseRate}/hr = $${quote.baseAmount.toFixed(2)}`);
    if (calcData.miles > 0) {
      notes.push(`Mileage: ${calcData.miles}mi × $${PRICING_CONFIG.mileage_rate}/mi = $${quote.mileageAmount.toFixed(2)}`);
    }
    if (calcData.selected_addons.length > 0) {
      const addonNames = calcData.selected_addons.map(a => PRICING_CONFIG.add_ons[a].label).join(', ');
      notes.push(`Add-ons: ${addonNames} = $${quote.addonsAmount.toFixed(2)}`);
    }
    if (calcData.is_urgent) {
      notes.push(`Urgent fee (50%): $${quote.urgentFee.toFixed(2)}`);
    }
    notes.push(`---`);
    notes.push(`Total: $${quote.total.toFixed(2)}`);
    
    setQuoteNotes(notes.join('\n'));
    setQuoteDuration(calcData.hours.toString());
    setShowCalculator(false);
    toast.success('Quote calculated and applied!');
  };

  // Create manual request
  const handleCreateManualRequest = async (e) => {
    e.preventDefault();
    if (!manualRequest.customer_name || !manualRequest.service_description) {
      toast.error('Please fill in customer name and service description');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/custom-requests/manual', {
        ...manualRequest,
        source: 'manual_entry'
      });
      toast.success('Custom request created!');
      setShowManualForm(false);
      setManualRequest({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        service_type: 'errands',
        service_description: '',
        preferred_date: '',
        preferred_time: '',
        address: '',
        city: '',
        state: 'NC',
        zip_code: '',
        budget_range: '',
        special_instructions: '',
        is_urgent: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAddon = (addonKey) => {
    setCalcData(prev => ({
      ...prev,
      selected_addons: prev.selected_addons.includes(addonKey)
        ? prev.selected_addons.filter(a => a !== addonKey)
        : [...prev.selected_addons, addonKey]
    }));
  };

  const openEditMode = (request) => {
    setSelectedRequest(request);
    setEditData({
      service_description: request.service_description || '',
      preferred_date: request.preferred_date || '',
      preferred_time: request.preferred_time || '',
      budget_range: request.budget_range || '',
      special_instructions: request.special_instructions || ''
    });
    setEditMode(true);
  };

  const getStatusBadge = (status) => {
    const s = statuses.find(st => st.value === status);
    if (!s) return <span className="px-2 py-1 rounded-full text-xs bg-[var(--background-subtle)]">{status}</span>;
    const Icon = s.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${s.color}`}>
        <Icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading custom requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[var(--primary)]" />
            Custom Requests
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Review and manage customer service request submissions</p>
        </div>
        <Button onClick={() => setShowManualForm(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> New Request
        </Button>
      </div>

      {/* Manual Request Form Modal */}
      {showManualForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowManualForm(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold">Create Custom Request</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowManualForm(false)}><X className="w-5 h-5" /></Button>
            </div>
            <form onSubmit={handleCreateManualRequest} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input value={manualRequest.customer_name} onChange={e => setManualRequest({...manualRequest, customer_name: e.target.value})} placeholder="John Doe" required />
                </div>
                <div>
                  <Label>Customer Email</Label>
                  <Input type="email" value={manualRequest.customer_email} onChange={e => setManualRequest({...manualRequest, customer_email: e.target.value})} placeholder="john@email.com" />
                </div>
                <div>
                  <Label>Customer Phone</Label>
                  <Input value={manualRequest.customer_phone} onChange={e => setManualRequest({...manualRequest, customer_phone: e.target.value})} placeholder="(252) 555-0100" />
                </div>
                <div>
                  <Label>Service Type</Label>
                  <Select value={manualRequest.service_type} onValueChange={v => setManualRequest({...manualRequest, service_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rides">Rides</SelectItem>
                      <SelectItem value="rides">Rides</SelectItem>
                      <SelectItem value="laundry">Laundry</SelectItem>
                      <SelectItem value="courier">Courier/Delivery</SelectItem>
                      <SelectItem value="errands">Errands & Tasks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Service Description *</Label>
                <Textarea value={manualRequest.service_description} onChange={e => setManualRequest({...manualRequest, service_description: e.target.value})} placeholder="Describe what the customer needs..." rows={3} required />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Preferred Date</Label>
                  <Input type="date" value={manualRequest.preferred_date} onChange={e => setManualRequest({...manualRequest, preferred_date: e.target.value})} />
                </div>
                <div>
                  <Label>Preferred Time</Label>
                  <Input type="time" value={manualRequest.preferred_time} onChange={e => setManualRequest({...manualRequest, preferred_time: e.target.value})} />
                </div>
              </div>
              
              <div>
                <Label>Service Address</Label>
                <Input value={manualRequest.address} onChange={e => setManualRequest({...manualRequest, address: e.target.value})} placeholder="123 Main St" />
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input value={manualRequest.city} onChange={e => setManualRequest({...manualRequest, city: e.target.value})} placeholder="Tarboro" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={manualRequest.state} onChange={e => setManualRequest({...manualRequest, state: e.target.value})} placeholder="NC" />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input value={manualRequest.zip_code} onChange={e => setManualRequest({...manualRequest, zip_code: e.target.value})} placeholder="27886" />
                </div>
              </div>
              
              <div>
                <Label>Budget Range</Label>
                <Input value={manualRequest.budget_range} onChange={e => setManualRequest({...manualRequest, budget_range: e.target.value})} placeholder="e.g., $50-100" />
              </div>
              
              <div>
                <Label>Special Instructions</Label>
                <Textarea value={manualRequest.special_instructions} onChange={e => setManualRequest({...manualRequest, special_instructions: e.target.value})} placeholder="Any special notes or requirements..." rows={2} />
              </div>
              
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_urgent" checked={manualRequest.is_urgent} onChange={e => setManualRequest({...manualRequest, is_urgent: e.target.checked})} className="w-4 h-4" />
                <label htmlFor="is_urgent" className="text-sm font-medium">Mark as Urgent</label>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowManualForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={submitting} className="flex-1 btn-primary">{submitting ? 'Creating...' : 'Create Request'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quote Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCalculator(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[var(--primary)]" />
              Quote Calculator
            </h3>
            
            <div className="space-y-4">
              {/* Service Type Selection */}
              <div className="p-4 bg-[var(--accent)] rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-[var(--primary)]" />
                  <span className="font-medium">Service Type</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(PRICING_CONFIG.service_types).map(([key, service]) => (
                    <label 
                      key={key} 
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        calcData.service_type === key 
                          ? 'bg-[var(--primary)] text-white border-[var(--primary)]' 
                          : 'bg-[var(--background-paper)] border-[var(--border-primary)] hover:border-[var(--primary)]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input 
                          type="radio" 
                          name="service_type" 
                          checked={calcData.service_type === key} 
                          onChange={() => setCalcData({...calcData, service_type: key, selected_addons: []})} 
                          className="sr-only" 
                        />
                        <span className="text-sm font-medium">{service.label}</span>
                      </div>
                      <span className={`text-xs font-bold ${calcData.service_type === key ? 'text-white' : 'text-[var(--primary)]'}`}>
                        ${service.base_rate}/hr
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Time (Hours) */}
              <div className="p-4 bg-[#D4AF37]/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock3 className="w-4 h-4 text-[#D4AF37]" />
                  <span className="font-medium">Time (Hours)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Input type="number" value={calcData.hours} onChange={e => setCalcData({...calcData, hours: parseFloat(e.target.value) || 0})} min={0.5} step={0.5} className="w-24" />
                  <span className="text-sm text-[var(--text-muted)]">× ${getBaseRate()}/hr = ${(getBaseRate() * calcData.hours).toFixed(2)}</span>
                </div>
              </div>
              
              {/* Mileage */}
              <div className="p-4 bg-[var(--success)]/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Car className="w-4 h-4 text-[var(--success)]" />
                  <span className="font-medium">Mileage</span>
                  <span className="text-xs text-[var(--text-muted)]">(from Tarboro, NC)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Input type="number" value={calcData.miles} onChange={e => setCalcData({...calcData, miles: parseFloat(e.target.value) || 0})} min={0} className="w-24" />
                  <span className="text-sm text-[var(--text-muted)]">miles × ${PRICING_CONFIG.mileage_rate}/mi = ${(PRICING_CONFIG.mileage_rate * calcData.miles).toFixed(2)}</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-2">First 10 miles free for recurring customers</p>
              </div>
              
              {/* Add-ons (filtered by service type) */}
              <div className="p-4 bg-[#F4C2C2]/10 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-[#F4C2C2]" />
                  <span className="font-medium">Add-ons for {PRICING_CONFIG.service_types[calcData.service_type]?.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {getRelevantAddons().map(([key, addon]) => (
                    <label key={key} className={`flex items-center justify-between p-2 rounded border cursor-pointer ${calcData.selected_addons.includes(key) ? 'bg-[#F4C2C2]/15 border-purple-400' : 'bg-[var(--background-paper)] border-[var(--border-primary)]'}`}>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={calcData.selected_addons.includes(key)} onChange={() => toggleAddon(key)} className="w-4 h-4" />
                        <span className="text-sm">{addon.label}</span>
                      </div>
                      <span className="text-sm font-medium">+${addon.price}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Urgent */}
              <div className="p-4 bg-[var(--warning)]/10 rounded-lg">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={calcData.is_urgent} onChange={e => setCalcData({...calcData, is_urgent: e.target.checked})} className="w-4 h-4" />
                    <span className="font-medium">Urgent / Same-Day Service</span>
                  </div>
                  <span className="text-sm text-amber-700 font-bold">+50% surcharge</span>
                </label>
              </div>
              
              {/* Quote Breakdown */}
              <div className="p-4 bg-[var(--background-subtle)] rounded-lg border-2 border-[var(--border-primary)]">
                <h4 className="font-bold mb-3">Quote Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{PRICING_CONFIG.service_types[calcData.service_type]?.label} ({calcData.hours}hrs × ${getBaseRate()})</span>
                    <span>${(getBaseRate() * calcData.hours).toFixed(2)}</span>
                  </div>
                  {calcData.miles > 0 && (
                    <div className="flex justify-between">
                      <span>Mileage ({calcData.miles} mi × ${PRICING_CONFIG.mileage_rate})</span>
                      <span>${(PRICING_CONFIG.mileage_rate * calcData.miles).toFixed(2)}</span>
                    </div>
                  )}
                  {calcData.selected_addons.length > 0 && calcData.selected_addons.map(addon => (
                    <div key={addon} className="flex justify-between text-purple-700">
                      <span>{PRICING_CONFIG.add_ons[addon].label}</span>
                      <span>+${PRICING_CONFIG.add_ons[addon].price.toFixed(2)}</span>
                    </div>
                  ))}
                  {calcData.is_urgent && (
                    <div className="flex justify-between text-amber-700">
                      <span>Urgent Surcharge (50%)</span>
                      <span>+${calculateQuote().urgentFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-[var(--primary)]">${calculateQuote().total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCalculator(false)} className="flex-1">Cancel</Button>
                <Button onClick={applyCalculatedQuote} className="flex-1 btn-primary">Apply Quote</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 text-center border-2 border-[var(--warning)]/30 bg-[var(--warning)]/10">
            <p className="text-3xl font-bold text-[var(--warning)]">{stats.pending}</p>
            <p className="text-sm text-yellow-700">Pending Review</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-[#F4C2C2]">{stats.quoted}</p>
            <p className="text-sm text-[var(--text-muted)]">Awaiting Response</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-[var(--success)]">{stats.converted}</p>
            <p className="text-sm text-[var(--text-muted)]">Converted</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold">{stats.total}</p>
            <p className="text-sm text-[var(--text-muted)]">Total</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={filter === '' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('')}
        >
          All
        </Button>
        {statuses.map(s => (
          <Button
            key={s.value}
            variant={filter === s.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(s.value)}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <div className="card p-12 text-center">
            <Sparkles className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Custom Requests</h3>
            <p className="text-[var(--text-muted)]">Custom requests from customers will appear here.</p>
          </div>
        ) : (
          requests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-5"
              data-testid={`request-${request.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(request.status)}
                    {request.quoted_price && (
                      <span className="text-sm font-bold text-[var(--success)]">${request.quoted_price}</span>
                    )}
                  </div>
                  <h3 className="font-bold text-lg">{request.title}</h3>
                  <p className="text-sm text-[var(--text-muted)] line-clamp-2 mt-1">{request.description}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {request.customer_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {request.city}, {request.state}
                    </span>
                    {request.preferred_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {request.preferred_date}
                      </span>
                    )}
                    {request.budget_range && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Budget: {request.budget_range}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {/* Action buttons based on status */}
                  {(request.status === 'pending' || request.status === 'reviewing') && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setQuotePrice(request.budget_range?.replace(/[^0-9.-]/g, '') || '');
                          setQuoteDuration(request.estimated_duration_hours?.toString() || '');
                          setEditMode(false);
                        }}
                        className="btn-primary"
                        data-testid={`quote-btn-${request.id}`}
                      >
                        <DollarSign className="w-4 h-4 mr-1" /> Send Quote
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[var(--success)] border-green-300"
                        onClick={() => handleApproveRequest(request.id)}
                        data-testid={`approve-btn-${request.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </>
                  )}
                  {request.status === 'quoted' && (
                    <span className="text-xs text-[#F4C2C2] font-medium">
                      Quoted: ${request.quoted_price}
                    </span>
                  )}
                  {request.status === 'converted' && request.booking_id && (
                    <span className="text-xs text-[var(--success)]">
                      Booking: {request.booking_id.slice(0, 8)}...
                    </span>
                  )}
                  
                  {/* Common action buttons */}
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditMode(request)}
                      title="Edit"
                      data-testid={`edit-request-${request.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedRequest(request);
                        setRescheduleData({
                          preferred_date: request.preferred_date || '',
                          preferred_time: request.preferred_time || ''
                        });
                        setShowRescheduleModal(true);
                      }}
                      title="Reschedule"
                      data-testid={`reschedule-request-${request.id}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-orange-500"
                      onClick={() => handleArchiveRequest(request.id)}
                      title="Archive"
                      data-testid={`archive-request-${request.id}`}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                      onClick={() => handleDeleteRequest(request.id)}
                      title="Delete"
                      data-testid={`delete-request-${request.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Quote Modal */}
      <AnimatePresence>
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="card p-6 max-w-lg w-full"
            >
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[var(--primary)]" />
                Send Quote
              </h2>

              <div className="bg-[var(--accent)] p-4 rounded-xl mb-6">
                <h3 className="font-bold">{selectedRequest.title}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">{selectedRequest.description}</p>
                <div className="flex gap-4 mt-3 text-sm">
                  <span><strong>Customer:</strong> {selectedRequest.customer_name}</span>
                  {selectedRequest.budget_range && (
                    <span><strong>Budget:</strong> {selectedRequest.budget_range}</span>
                  )}
                </div>
                <p className="text-sm mt-2">
                  <strong>Location:</strong> {selectedRequest.address}, {selectedRequest.city}, {selectedRequest.state} {selectedRequest.zip_code}
                </p>
              </div>

              {/* Photos Section */}
              {((selectedRequest.photos?.length > 0) || (selectedRequest.before_photos?.length > 0) || (selectedRequest.after_photos?.length > 0)) && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Photos
                  </h4>
                  <div className="space-y-3">
                    {selectedRequest.photos?.length > 0 && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] mb-2">Customer Photos:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedRequest.photos.map((photo) => (
                            <div key={photo.id || photo.url} className="relative group">
                              <img src={photo.url} alt={photo.description || 'Photo'} className="w-16 h-16 object-cover rounded-lg border" />
                              <button onClick={() => handleRemovePhoto(photo.id, 'request')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedRequest.before_photos?.length > 0 && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] mb-2">Before Photos:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedRequest.before_photos.map((photo) => (
                            <div key={photo.id || photo.url} className="relative group">
                              <img src={photo.url} alt={photo.description || 'Before'} className="w-16 h-16 object-cover rounded-lg border border-yellow-400" />
                              <button onClick={() => handleRemovePhoto(photo.id, 'before')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedRequest.after_photos?.length > 0 && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] mb-2">After Photos:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedRequest.after_photos.map((photo) => (
                            <div key={photo.id || photo.url} className="relative group">
                              <img src={photo.url} alt={photo.description || 'After'} className="w-16 h-16 object-cover rounded-lg border border-green-400" />
                              <button onClick={() => handleRemovePhoto(photo.id, 'after')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Add Photo Button */}
              <div className="mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPhotoModal(true)}
                  className="w-full"
                >
                  <Camera className="w-4 h-4 mr-2" /> Add Photo (Before/After)
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Quote Price ($) *</label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCalculator(true)}
                      className="text-xs"
                      data-testid="open-calculator-btn"
                    >
                      <Calculator className="w-3 h-3 mr-1" /> Use Calculator
                    </Button>
                  </div>
                  <Input
                    type="number"
                    value={quotePrice}
                    onChange={(e) => setQuotePrice(e.target.value)}
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    data-testid="quote-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Duration (hours)</label>
                  <Input
                    type="number"
                    value={quoteDuration}
                    onChange={(e) => setQuoteDuration(e.target.value)}
                    placeholder="e.g., 2"
                    min="0.5"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes for Customer</label>
                  <Textarea
                    value={quoteNotes}
                    onChange={(e) => setQuoteNotes(e.target.value)}
                    placeholder="Any details about the quote, what's included, etc."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedRequest(null);
                    setQuotePrice('');
                    setQuoteNotes('');
                    setQuoteDuration('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendQuote}
                  disabled={submitting || !quotePrice}
                  className="btn-primary flex-1"
                  data-testid="send-quote-btn"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send Quote
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Request Modal */}
      <AnimatePresence>
        {editMode && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-xl w-full max-w-lg"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold">Edit Custom Request</h2>
                <Button variant="ghost" size="sm" onClick={() => { setEditMode(false); setSelectedRequest(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Service Description</label>
                  <Textarea
                    value={editData.service_description || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, service_description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Preferred Date</label>
                    <Input
                      type="date"
                      value={editData.preferred_date || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, preferred_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Preferred Time</label>
                    <Input
                      type="time"
                      value={editData.preferred_time || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, preferred_time: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Budget Range</label>
                  <Input
                    value={editData.budget_range || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, budget_range: e.target.value }))}
                    placeholder="e.g. $50-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Special Instructions</label>
                  <Textarea
                    value={editData.special_instructions || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, special_instructions: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setEditMode(false); setSelectedRequest(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleEditRequest} className="btn-primary">
                  Save Changes
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reschedule Modal */}
      <AnimatePresence>
        {showRescheduleModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-xl w-full max-w-md"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold">Reschedule Request</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowRescheduleModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">New Date</label>
                  <Input
                    type="date"
                    value={rescheduleData.preferred_date}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, preferred_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">New Time</label>
                  <Input
                    type="time"
                    value={rescheduleData.preferred_time}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, preferred_time: e.target.value }))}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRescheduleModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleReschedule} className="btn-primary">
                  <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Photo Modal */}
      <AnimatePresence>
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="card p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-[var(--primary)]" />
                Add Photo
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Photo Type</Label>
                  <Select value={photoType} onValueChange={setPhotoType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="request">Customer Photo</SelectItem>
                      <SelectItem value="before">Before Photo</SelectItem>
                      <SelectItem value="after">After Photo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Photo URL *</Label>
                  <Input
                    value={newPhotoUrl}
                    onChange={(e) => setNewPhotoUrl(e.target.value)}
                    placeholder="https://example.com/photo.jpg"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Paste a link to the photo (Google Drive, Dropbox, etc.)
                  </p>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newPhotoDescription}
                    onChange={(e) => setNewPhotoDescription(e.target.value)}
                    placeholder="Brief description of the photo"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPhotoModal(false);
                    setNewPhotoUrl('');
                    setNewPhotoDescription('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddPhoto}
                  disabled={!newPhotoUrl}
                  className="btn-primary flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" /> Add Photo
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomRequestsTab;
